import {Router} from '@angular/router';
import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

import {Subscription} from 'rxjs';

import {
    ApiError,
    TradingService,
    AuthApiService,
    CompaniesService,
    IndividualsService,
    TradingClientModel,
    CompanyExtendedModel,
    IndividualExtendedModel
} from '@spa/api';

import {Helper} from '../../../../shared/classes';
import {FormComponent} from '../../../../shared/widgets/form';
import {FormInterface, PermissionRuleInterface} from '../../../../shared/interfaces';
import {DialogActionsEnum, DialogComponent, DialogConfigInterface} from '../../../../shared/components';

import {ChangeClientStatusForm} from '../../forms';
import {ChangeClientStatusFormErrorStatesEnum} from '../../enums';

import {ChangeClientStatusMessages} from '../../../../../assets/messages';

@Component({
    selector: 'app-change-client-status',
    templateUrl: './change-client-status.component.html',
    styleUrls: ['./change-client-status.component.scss']
})
export class ChangeClientStatusComponent extends DialogComponent<ChangeClientStatusComponent>
    implements OnInit, OnDestroy, FormInterface {

    private changeAccountStatusSubscription: Subscription;

    public formErrorCode: number;
    public formErrorCodesEnum: typeof ChangeClientStatusFormErrorStatesEnum;
    public form: ChangeClientStatusForm;
    public hasClientStatusError: boolean;
    public additionalPermissionsMessage: string;

    constructor(
        protected router: Router,
        private individualsService: IndividualsService,
        private tradingService: TradingService,
        private companiesService: CompaniesService,
        private authApiService: AuthApiService,
        @Inject(MAT_DIALOG_DATA) public data: {
            config: DialogConfigInterface,
            client: IndividualExtendedModel | CompanyExtendedModel,
            tradingClient: TradingClientModel,
            triggers: number[],
            isCorporateClient: boolean
            accountDependencyPermissionRule: PermissionRuleInterface,
            changeClientStatusToTerminatedPermissionRule: PermissionRuleInterface
        },
        public dialogRef: MatDialogRef<ChangeClientStatusComponent>
    ) {
        super(dialogRef);

        this.config = data.config;
        this.formErrorCodesEnum = ChangeClientStatusFormErrorStatesEnum;
        this.config.process.succeedMessage = ChangeClientStatusMessages.ClientStatusChangeSucceed.message;
        this.additionalPermissionsMessage = ChangeClientStatusMessages.AddTradingAccountAdditionalPermissions.message;
    }

    private changeClientStatus(): void {
        const authUserPermissions = !this.authApiService.getAuthenticatedUser() ? [] :
            this.authApiService.getAuthenticatedUser().permissions;

        const changeStatusToTerminatedPermissions =
            this.data.changeClientStatusToTerminatedPermissionRule.permissions;

        if (this.validateForm()) {
            this.startLoading(() => {
                this.changeAccountStatusSubscription = this.form
                    .createRequest(authUserPermissions, changeStatusToTerminatedPermissions)
                    .subscribe(errors => {
                        if (errors.length !== 0) {
                            const errorStates = errors.map(error => error.status);

                            const canFindTradingAccountError =
                                ChangeClientStatusFormErrorStatesEnum.CantFindTradingClient;

                            const changeStatusToTerminatedRestrictedError =
                                ChangeClientStatusFormErrorStatesEnum.ChangeStatusToTerminatedRestricted;

                            if (errorStates.indexOf(canFindTradingAccountError) >= 0) {
                                this.config.buttonsHidden = true;
                                this.hasClientStatusError = true;
                                this.formErrorCode = canFindTradingAccountError;

                                this.config.process.failedMessage =
                                    ChangeClientStatusMessages.CantFindTradingClient.message;
                            } else if (errorStates.indexOf(changeStatusToTerminatedRestrictedError) >= 0) {
                                this.config.buttonsHidden = true;
                                this.hasClientStatusError = true;
                                this.formErrorCode = changeStatusToTerminatedRestrictedError;

                                this.config.process.failedMessage =
                                    ChangeClientStatusMessages.ChangeStatusToTerminatedRestricted.message;
                            } else {
                                this.config.process.failedMessage = errors.map((error: ApiError) => {
                                    return this.createFailedDialogMessage(error);
                                }).join('</br>');

                                this.config.process.failed = true;
                                this.config.process.finished = true;
                            }
                        } else {
                            this.config.process.finished = true;
                        }

                        this.config.process.loading = false;
                    });
            });
        }
    }

    private createFailedDialogMessage(error: ApiError): string {
        switch (error.status) {
            case ChangeClientStatusFormErrorStatesEnum.UpdateClientStatusFailed:
                return ChangeClientStatusMessages.ClientStatusChangeFailed.message;
            case ChangeClientStatusFormErrorStatesEnum.CantFindGroupsAndAccountsForActivation:
                return ChangeClientStatusMessages.CantFindGroupsAndAccountsForActivation.message;
            case ChangeClientStatusFormErrorStatesEnum.UpdateTradingGroupStatusFailed:
                return ChangeClientStatusMessages.TradingGroupStatusUpdateFailed.message
                    .replace('<<reference>>', error.data);
            case ChangeClientStatusFormErrorStatesEnum.UpdateTradingAccountStatusFailed:
                return ChangeClientStatusMessages.TradingAccountStatusUpdateFailed.message
                    .replace('<<reference>>', error.data);
            default:
                return error.data;
        }
    }

    ngOnInit(): void {
        this.form = new ChangeClientStatusForm(
            this.data.client,
            this.data.tradingClient,
            this.individualsService,
            this.tradingService,
            this.companiesService,
            this.data.isCorporateClient
        );

        this.initForm(this.data.triggers);
    }

    ngOnDestroy(): void {
        if (this.changeAccountStatusSubscription) {
            this.changeAccountStatusSubscription.unsubscribe();
        }
    }

    initForm(statusTriggers?: number[]): void {
        if (statusTriggers) {
            const currentTrigger = !this.data.isCorporateClient ? Helper.enumsHelper()
                    .getClientStatusTriggerByStatus((this.data.client as IndividualExtendedModel).individualStatus) :
                Helper.enumsHelper().getClientStatusTriggerByStatus((this.data.client as CompanyExtendedModel).status);

            statusTriggers = [].concat(...statusTriggers, currentTrigger);
        }

        this.form.initForm(statusTriggers);
    }

    getFormMessages(controlLabel?: string): { [key: string]: string } {
        const differentStatusCheckFailedKey = ChangeClientStatusMessages.DifferentStatusCheckFailed.key;
        const differentStatusCheckFailedMessage = ChangeClientStatusMessages.DifferentStatusCheckFailed.message;

        return {
            ...FormComponent.getFormMessages(controlLabel),
            [differentStatusCheckFailedKey]: differentStatusCheckFailedMessage
        };
    }

    validateForm(): boolean {
        return this.form.validateForm(
            this.getFormMessages,
            ChangeClientStatusMessages.DifferentStatusCheckFailed.key
        );
    }

    onFormSubmit(action?: number): void {
        switch (action) {
            case DialogActionsEnum.Decline:
                this.dialogRef.close(false);
                break;
            case DialogActionsEnum.ProcessFinished:
                this.dialogRef.close(!this.config.process.failed);
                break;
            case DialogActionsEnum.Confirm:
                this.changeClientStatus();
                break;
        }
    }
}
